import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box, MenuItem, Select, FormControl, InputLabel, Grid, Alert, IconButton
} from '@mui/material';
import { Add, Remove, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import BSDateInput from '../common/BSDateInput';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];
const unitOptions = ['Pcs', 'Pair', 'Set'];
const billNameOptions = ['P', 'K', 'R', 'O', 'W', 'Y'];

const emptyItem = () => ({ itemName: '', karat: '', quantity: '', unit: '', itemWeight: '', description: '', wastage: '' });

const ShopDirectOrder = () => {
  const [karigar, setKarigar] = useState('');
  const [karigars, setKarigars] = useState([]);
  const [salesman, setSalesman] = useState('');
  const [order, setOrder] = useState({ billName: '', billNumber: '', orderDate: '', deliveryDate: '', requiredDate: '' });
  const [items, setItems] = useState([emptyItem()]);
  const [itemOptions, setItemOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/karigar').then(res => setKarigars(res.data)).catch(() => {});
    api.get('/api/items').then(res => setItemOptions(res.data.map(i => i.name))).catch(() => {});
  }, []);

  const handleOrderChange = e => {
    const { name, value } = e.target;
    setOrder(o => ({ ...o, [name]: value }));
  };

  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    setItems(items => items.map((item, i) => i === idx ? { ...item, [name]: value } : item));
  };

  const addItem = () => setItems(items => [...items, emptyItem()]);
  const removeItem = idx => setItems(items => items.length > 1 ? items.filter((_, i) => i !== idx) : items);

  const validate = () => {
    if (!karigar) return 'Karigar is required.';
    if (!salesman) return 'Salesman is required.';
    if (!order.billNumber || !order.orderDate || !order.deliveryDate || !order.requiredDate) return 'All order fields are required.';
    for (const item of items) {
      if (!item.itemName || !item.karat || !item.quantity || !item.unit || !item.itemWeight) return 'All item fields are required.';
    }
    return '';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(''); setSuccess('');
    const err = validate();
    if (err) { setError(err); return; }
    setLoading(true);
    try {
      const computedItems = items.map(item => {
        const weight = parseFloat(item.itemWeight) || 0;
        const wastage = parseFloat(item.wastage) || 0;
        const total = weight + (weight * wastage / 100);
        return {
          ...item,
          quantity: Number(item.quantity),
          itemWeight: weight,
          wastage: wastage,
          total: parseFloat(total.toFixed(3)),
          netAmount: 0,
          totalAmount: 0,
          gsb: 0,
          rate: 0,
          makingCharge: 0,
          stoneAmount: 0,
          despatchStatus: false,
          shopDespatchStatus: false,
          workIssued: false,
          shopWorkIssued: false,
          karigarAbbreviation: '',
          shopKarigarAbbreviation: karigar
        };
      });
      await api.post('/api/shop/direct-orders', {
        billName: order.billName || undefined,
        billNumber: order.billNumber,
        orderDate: order.orderDate,
        deliveryDate: order.deliveryDate,
        requiredDate: order.requiredDate,
        status: 'Pending',
        agentName: salesman,
        karigarAbbreviation: karigar,
        items: computedItems,
        totalAmount: 0,
        isUrgent: false,
        isActive: true,
        createdBy: 'shop'
      });
      setSuccess('Order created successfully!');
      setKarigar(''); setSalesman('');
      setOrder({ billName: '', billNumber: '', orderDate: '', deliveryDate: '', requiredDate: '' });
      setItems([emptyItem()]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create order');
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" size="small">Back</Button>
        </Box>
        <Typography variant="h5" gutterBottom color="primary">Shop Direct Order Entry</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Bill Name</InputLabel>
                <Select name="billName" value={order.billName} label="Bill Name" onChange={handleOrderChange}>
                  <MenuItem value="">None</MenuItem>
                  {billNameOptions.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField label="Bill Number" name="billNumber" value={order.billNumber} onChange={handleOrderChange} required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth required size="small">
                <InputLabel>Karigar</InputLabel>
                <Select value={karigar} label="Karigar" onChange={e => setKarigar(e.target.value)}>
                  {karigars.map(k => <MenuItem key={k.abbreviation} value={k.abbreviation}>{k.abbreviation} - {k.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Salesman" value={salesman} onChange={e => setSalesman(e.target.value)} required fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput label="Order Date" value={order.orderDate} onChange={date => setOrder(o => ({ ...o, orderDate: date }))} required />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput label="Delivery Date" value={order.deliveryDate} onChange={date => setOrder(o => ({ ...o, deliveryDate: date }))} required />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BSDateInput label="Required Date" value={order.requiredDate} onChange={date => setOrder(o => ({ ...o, requiredDate: date }))} required />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Order Items</Typography>
          {items.map((item, idx) => (
            <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5, mb: 1.5 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={6} sm={2}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Item Name</InputLabel>
                    <Select name="itemName" value={item.itemName} label="Item Name" onChange={e => handleItemChange(idx, e)}>
                      {itemOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={1}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Karat</InputLabel>
                    <Select name="karat" value={item.karat} label="Karat" onChange={e => handleItemChange(idx, e)}>
                      {karatOptions.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4} sm={1}>
                  <TextField label="Qty" name="quantity" type="number" value={item.quantity} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
                </Grid>
                <Grid item xs={4} sm={1}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Unit</InputLabel>
                    <Select name="unit" value={item.unit} label="Unit" onChange={e => handleItemChange(idx, e)}>
                      {unitOptions.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4} sm={1}>
                  <TextField label="Weight" name="itemWeight" type="number" value={item.itemWeight} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
                </Grid>
                <Grid item xs={6} sm={1}>
                  <TextField label="Wastage %" name="wastage" type="number" value={item.wastage} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                </Grid>
                <Grid item xs={6} sm={1}>
                  <TextField
                    label="Wastage Wt"
                    value={item.itemWeight && item.wastage ? ((parseFloat(item.itemWeight) * parseFloat(item.wastage)) / 100).toFixed(3) : ''}
                    InputProps={{ readOnly: true }}
                    fullWidth size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField label="Description" name="description" value={item.description} onChange={e => handleItemChange(idx, e)} fullWidth size="small" />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <Box sx={{ display: 'flex' }}>
                    <IconButton size="small" onClick={() => removeItem(idx)} disabled={items.length === 1}><Remove fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={addItem}><Add fontSize="small" /></IconButton>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Order'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default ShopDirectOrder;
